import { Button, DatePicker, Spin } from 'antd';
import { Select } from 'antd';
import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { isEmpty } from 'underscore';
import { Label } from '../../../../components/Label/Label';
import { IPlotVariable } from '../../../../typings/analytics';
import styles from '../../Style/AnalyticsPanel.module.scss';
import { ChartBox, AnalyticsDownloadLinks } from '../ChartBox';
import { IBestDataset } from '../../../../typings/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../typings/chart';
import { IEnrichedMetadata } from '../../../../typings/metadata';
import { ADVANCED_ANALYTICS } from '../AnalyticsPanel';
import moment, { Moment } from 'moment';
import { DateFormat, DT0Format } from '../../../../generic/StaticVariable';
import { IAnalytcisUserInput } from '../../../../typings/IAnalyticsUserInput';
import { DATASET_CATEGORIES } from '../../../../generic/filterBestDataset';


const limitationMessage =
  'Tidal parameters are available to Water Level (WL) & Current Speed (CS) only';
const qualityDefinitionLabel = 'Definition_of_Tidal_Levels';
const qualityDefinitionLink = 'https://www.metocean-on-demand.com/fileshare/getfile.ashx?type=image&file_name=Definition_of_tidal_levels.png';
const supportedItems = ['WL'];

export default function TidalLevel(props: {
  coordinates: number[];
  previewDatasetId: string;
  datasets: IBestDataset[];
  onDatasetChange: (item: string) => void
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  tidalLevelValueImage: string;
  tidalValueLinks: string[];
  error: string;
  updating: boolean;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
}) {
  const {
    datasets,
    dateRange,
    previewDatasetId,
    coordinates,
    enrichedMetadata,
    onUpdate,
    tidalLevelValueImage,
    tidalValueLinks,
    error,
    pointName,
    userInput,
    updateUserInput,
    updating,
    onDatasetChange,
  } = props;
  const [listVariablesX, setListVariableX] = useState<IPlotVariable[]>([]);
  const [dataset, setDataset] = useState(userInput.TidalLevel.dataset);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.TidalLevel.variable
  );
  const [startDate, setStartDate] = useState(
    userInput.TidalLevel.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(
    userInput.TidalLevel.endDate || moment(dateRange[1])
  );
  const [supported, setSupported] = useState<boolean>(false)

  useEffect(() => {
    updateUserInput({
      TidalLevel: {
        dataset: dataset,
        variable: variableX,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [dataset, variableX, startDate, endDate]);

  const getImage = useCallback(
    (
      indexX: IPlotVariable,
      orderId: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.TIDAL_LEVEL,
          DatasetId: previewDatasetId,
          OriginalDatasetId: previewDatasetId,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, previewDatasetId, pointName]
  );

  useEffect(() => {
    if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates) && tidalLevelValueImage === '') {
      setDataset(previewDatasetId);
      if (enrichedMetadata) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === previewDatasetId &&
            task.CoordinatesString === props.coordinates.join()
        );
        if (task) {
          const itemsInfos = task.ItemsInfo.filter(
            (itemInfo: IPlotVariable) =>
              itemInfo.ShortName && supportedItems.includes(itemInfo.ShortName)
          );
          if (itemsInfos.length > 0) {
            setSupported(true)
            setListVariableX(itemsInfos);
            const indexX = itemsInfos[0];
            setVariableX(indexX);
            getImage(
              indexX,
              task.OrderId,
              startDate.format(DT0Format),
              endDate.format(DT0Format)
            );
          } else {
            setSupported(false)
          }
        }
      }
    }
  }, [previewDatasetId, datasets, enrichedMetadata, coordinates, tidalLevelValueImage]);

  const onUpdateTidalLevel = useCallback(
    () => {
      if (enrichedMetadata && variableX) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === dataset &&
            task.CoordinatesString === props.coordinates.join()
        );

        if (task) {
          getImage(
            variableX,
            task.OrderId,
            startDate.format(DT0Format),
            endDate.format(DT0Format),
          );
        }
      }
    },
    [enrichedMetadata, dataset, startDate, endDate, variableX, pointName]
  );

  const doVariableChange = (active: number) => {
    const item = listVariablesX.find(e => e.Index === active)
    setVariableX(item!);
  };

  const doStartDateChange = (value: any) => {
    if (value) {
      setStartDate(value)
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onSelectDataset = (value: string) => {
    onDatasetChange(value)
  }

  function disabledStartDate(current: Moment) {
    // Can not select date after end date
    const isAfterEndDate = current && current > endDate;
    // and not before preview dataset's time range and not after preview dataset's time range
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(dateRange[0], 'day');
  }

  function disabledEndDate(current: Moment) {
    // Can not select date before start date and not after preview dataset's time range
    const isBeforeStartDate = current && current < startDate;
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(dateRange[1], 'day');
  }

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => {
      return bd.label;
    })

    return Array.from(new Set(label));
  }, [datasets])

  const actionRows = (
    <Fragment>
      <div className={styles.actionRow}>
        <Label label="Dataset" className={styles.flex1}>
          <Select
            value={dataset}
            size="small"
            style={{ width: '100%' }}
            onChange={onSelectDataset}
          >
             {categories.map((category: string) => {
              const section = category === 'SW'
                ? DATASET_CATEGORIES.WAVES
                : category === 'HD'
                  ? DATASET_CATEGORIES.OCEAN
                  : DATASET_CATEGORIES.ATMOSPHERE;
              return (
                <Select.OptGroup key={category} label={section}>
                  {datasets
                    .filter((item) => {
                      return item.label === category;
                    })
                    .map((item) => {
                      return (
                        <Select.Option key={item.id} value={item.id}>
                          {item.value}
                        </Select.Option>
                      );
                    })}
                </Select.OptGroup>
              );
            })}
          </Select>
        </Label>
        <Label label="Variable" className={styles.flex1}>
          <Select
            value={variableX?.Index}
            size="small"
            style={{ width: '100%' }}
            onChange={doVariableChange}
          >
            {listVariablesX.map((item, id) => {
              return (
                <Select.Option key={item.Index} value={item.Index}>
                  {item.Name}
                </Select.Option>
              );
            })}
          </Select>
        </Label>
      </div>
      <div className={styles.actionRow}>
        <Label label="Start Date" className={styles.flex1}>
          <DatePicker
            size="small"
            value={moment(startDate)}
            disabledDate={disabledStartDate}
            onChange={doStartDateChange}
            format={DateFormat}
          />
        </Label>
        <Label label="End Date" className={styles.flex1}>
          <DatePicker
            size="small"
            value={moment(endDate)}
            disabledDate={disabledEndDate}
            onChange={doEndDateChange}
            format={DateFormat}
          />
        </Label>
      </div>
      <div className={styles.analyticsSubHeadlineContainer}>
        <div className={styles.analyticsPointName}>{pointName}</div>
        <AnalyticsDownloadLinks links={tidalValueLinks} />
        <Button type="primary" onClick={onUpdateTidalLevel} loading={updating}>
          {'Update'}
        </Button>
      </div>
    </Fragment>
  );

  return !supported ? (
    <div>{limitationMessage}</div>
  ) : (
    <ChartBox
      actionRow={actionRows}
      key={'Tidal Level'}
      title={'Tidal Level'}
      height={updating || !isEmpty(error) ? '50px' : 'auto'}
      plot={
        updating ? (
          <div className={styles.spinner}>
            <Spin size="large" />
          </div>
        ) : !isEmpty(error) ? (
          <div>{error}</div>
        ) : tidalLevelValueImage && (
          <img
            style={{ width: '80%', objectFit: 'scale-down', maxHeight: '65vh' }}
            src={tidalLevelValueImage}
            alt="Tidal Level"
          />
        )
      }
      dataset={''}
      showDisclaimer
      limitation={
        <Button type="link" href={qualityDefinitionLink}>
          {qualityDefinitionLabel}
        </Button>
      }
    ></ChartBox>
  );
}
